// SEO pillar pages content (French — primary search market: Bruxelles).
// Each page targets a high-intent query, links to the Restart test, and feeds
// FAQPage + BreadcrumbList structured data for Google and AI answer engines.

const SITE = "https://www.bhealth.be";

export const PILLARS = {
  "sport-sante-bruxelles": {
    slug: "sport-sante-bruxelles",
    eyebrow: "Guide santé · Bruxelles",
    h1: "Sport santé à Bruxelles : bouger pour votre santé, à votre rythme",
    title: "Sport santé à Bruxelles : forme, mobilité & énergie | B.health®",
    description:
      "Le sport santé à Bruxelles avec B.health® (Berchem-Sainte-Agathe) : activité physique adaptée, kinésithérapie, ostéopathie, nutrition et coaching. Faites le test Restart gratuit (3 min).",
    keywords:
      "sport santé Bruxelles, activité physique adaptée, centre santé Berchem-Sainte-Agathe, kinésithérapie Bruxelles, ostéopathie, coaching santé",
    image:
      "/img/pillar-energie.jpg",
    intro:
      "Le « sport santé » ne consiste pas à performer, mais à utiliser l'activité physique comme un véritable outil de prévention et de mieux-être. À Bruxelles, B.health® vous accompagne avec une approche progressive, encadrée et adaptée à votre corps — quel que soit votre âge ou votre point de départ.",
    sections: [
      {
        h: "Qu'est-ce que le sport santé ?",
        p: [
          "Le sport santé désigne une pratique d'activité physique pensée d'abord pour entretenir et améliorer votre santé : retrouver de l'énergie, préserver votre mobilité, gérer votre poids ou simplement vous sentir mieux au quotidien.",
          "Contrairement au sport de performance, l'objectif n'est pas le chrono ou la compétition, mais la régularité, le plaisir et la sécurité. C'est une démarche durable, conçue pour s'intégrer dans votre vie réelle.",
        ],
      },
      {
        h: "Pourquoi se faire accompagner à Bruxelles ?",
        p: [
          "Reprendre seul une activité après une longue pause expose souvent aux blessures, au découragement ou à l'abandon. Un cadre professionnel fait toute la différence : un bilan de départ, un programme adapté et un suivi régulier.",
          "À Berchem-Sainte-Agathe, B.health® réunit sur 800 m² une équipe pluridisciplinaire — kinésithérapeutes, ostéopathe, nutritionniste, coachs et praticiens bien-être — pour vous accompagner de A à Z, au même endroit.",
        ],
      },
      {
        h: "L'approche B.health : prévention, progression, accompagnement",
        p: [
          "Tout commence par un bilan personnalisé qui identifie vos objectifs, vos freins et votre niveau actuel. À partir de là, nous construisons une reprise progressive et sécurisée.",
          "Activité physique adaptée, cours collectifs, personal training, conseils nutrition (Glyco-Coaching) et soins paramédicaux se combinent dans un même écosystème santé, pour des résultats durables plutôt que des promesses miracles.",
        ],
      },
      {
        h: "Pour qui ?",
        p: [
          "• Les personnes qui reprennent une activité après une pause, une blessure ou une période difficile.",
          "• Celles et ceux qui veulent gérer leur poids, retrouver de l'énergie ou préserver leur mobilité avec l'âge.",
          "• Les seniors souhaitant rester autonomes et en forme, en toute sécurité.",
          "• Toute personne qui préfère un cadre encadré et bienveillant plutôt qu'une salle anonyme.",
        ],
      },
      {
        h: "Comment commencer ?",
        p: [
          "La première étape est simple et gratuite : faites le test Restart en 3 minutes. Vous découvrez votre profil santé et recevez des conseils personnalisés ainsi qu'une première étape adaptée.",
          "Notre équipe vous recontacte ensuite pour vous proposer votre bilan, sans engagement.",
        ],
      },
    ],
    faqs: [
      {
        q: "Le sport santé est-il adapté si je n'ai jamais fait de sport ?",
        a: "Oui. L'approche sport santé de B.health est justement conçue pour les débutants et les reprises : on part de votre niveau réel, avec une progression douce et sécurisée, encadrée par des professionnels.",
      },
      {
        q: "Où se situe le centre B.health à Bruxelles ?",
        a: "B.health se trouve Avenue de la Basilique 14, 1082 Berchem-Sainte-Agathe, facilement accessible en voiture et en transports en commun. Un itinéraire Google Maps et Waze est disponible sur le site.",
      },
      {
        q: "Faut-il un certificat médical ?",
        a: "Selon votre situation, un avis professionnel peut être recommandé avant de reprendre. Notre démarche reste non médicale et préventive ; en cas de douleur importante ou persistante, nous vous orientons vers un avis adapté.",
      },
    ],
  },

  "perte-de-poids-apres-40-ans": {
    slug: "perte-de-poids-apres-40-ans",
    eyebrow: "Guide santé · Poids",
    h1: "Perdre du poids après 40 ans, durablement et sans pression",
    title: "Perte de poids après 40 ans : méthode durable | B.health® Bruxelles",
    description:
      "Perte de poids après 40 ans à Bruxelles : pourquoi les régimes échouent et comment B.health® combine nutrition (Glyco-Coaching) et activité adaptée pour des résultats durables. Test Restart gratuit.",
    keywords:
      "perte de poids après 40 ans, maigrir après 40 ans, métabolisme, Glyco-Coaching, nutrition Bruxelles, activité physique perte de poids",
    image:
      "/img/pillar-poids.jpg",
    intro:
      "Après 40 ans, le corps change : métabolisme plus lent, masse musculaire qui diminue, rythme de vie chargé. Résultat, les méthodes d'avant ne fonctionnent plus aussi bien. La bonne nouvelle : avec la bonne approche, perdre du poids durablement reste tout à fait possible — sans régime extrême.",
    sections: [
      {
        h: "Pourquoi est-ce différent après 40 ans ?",
        p: [
          "Avec l'âge, le métabolisme ralentit et la masse musculaire tend à diminuer, ce qui réduit la dépense énergétique au repos. Le stress, le sommeil et les variations hormonales jouent aussi un rôle.",
          "Ce n'est pas une fatalité : c'est simplement le signe qu'il faut une stratégie différente, qui combine alimentation intelligente et activité physique adaptée.",
        ],
      },
      {
        h: "L'erreur des régimes restrictifs",
        p: [
          "Les régimes très restrictifs font souvent perdre du muscle plutôt que de la graisse, ralentissent encore le métabolisme et provoquent l'effet « yo-yo ». La frustration finit par l'emporter.",
          "L'objectif n'est pas de manger moins à tout prix, mais de manger mieux et de bouger de façon adaptée, pour relancer durablement votre corps.",
        ],
      },
      {
        h: "L'approche B.health : nutrition + activité adaptée",
        p: [
          "B.health associe le « Glyco-Coaching » (un accompagnement nutritionnel centré sur la gestion de la glycémie et des habitudes durables) à une activité physique adaptée qui préserve et renforce la masse musculaire.",
          "Cette combinaison favorise une perte de poids progressive et stable, tout en améliorant l'énergie, la silhouette et le bien-être général. Le tout encadré, à votre rythme.",
        ],
      },
      {
        h: "Votre profil Restart « Poids »",
        p: [
          "Le test Restart identifie si votre profil dominant est lié à la gestion du poids, et adapte les conseils à votre tranche d'âge et à vos contraintes.",
          "Vous repartez avec une première étape concrète et un accompagnement possible auprès de notre équipe nutrition et coaching.",
        ],
      },
      {
        h: "Par où commencer ?",
        p: [
          "Commencez par le test Restart, gratuit et en 3 minutes. Vous recevrez votre profil et des conseils personnalisés, puis notre équipe vous proposera votre bilan, sans engagement.",
        ],
      },
    ],
    faqs: [
      {
        q: "Peut-on vraiment perdre du poids après 40 ans ?",
        a: "Oui. Après 40 ans, il faut surtout une stratégie adaptée : préserver le muscle, manger mieux plutôt que moins, et bouger régulièrement. C'est l'approche que B.health met en place avec vous, de façon progressive et durable.",
      },
      {
        q: "Qu'est-ce que le Glyco-Coaching ?",
        a: "Le Glyco-Coaching est un accompagnement nutritionnel proposé chez B.health, centré sur la gestion de la glycémie et l'installation d'habitudes alimentaires durables, plutôt que sur des régimes restrictifs ponctuels.",
      },
      {
        q: "Combien de temps avant de voir des résultats ?",
        a: "Cela dépend de chaque personne et de son point de départ. L'objectif de B.health est une progression durable et raisonnable plutôt qu'une perte rapide non maintenable. Aucun résultat médical n'est promis ou garanti.",
      },
    ],
  },

  "reprise-du-sport-en-douceur": {
    slug: "reprise-du-sport-en-douceur",
    eyebrow: "Guide santé · Mobilité & reprise",
    h1: "Reprendre le sport en douceur, sans se blesser",
    title: "Reprendre le sport en douceur après une pause | B.health® Bruxelles",
    description:
      "Reprendre le sport après une longue pause à Bruxelles, sans se blesser : reprise progressive, encadrement kiné et coaching chez B.health® (Berchem-Sainte-Agathe). Test Restart gratuit.",
    keywords:
      "reprendre le sport en douceur, reprise du sport après une pause, retrouver mobilité, sport senior Bruxelles, kinésithérapie, reprise activité physique",
    image:
      "/img/pillar-mobilite.jpg",
    intro:
      "Reprendre une activité physique après des mois — ou des années — d'arrêt est l'une des meilleures décisions pour votre santé. Mais reprendre trop vite est la première cause de blessure et d'abandon. La clé : une reprise progressive et encadrée.",
    sections: [
      {
        h: "Pourquoi reprendre progressivement ?",
        p: [
          "Après une longue pause, vos muscles, articulations et tendons ont besoin de temps pour se réhabituer à l'effort. Vouloir « rattraper le temps perdu » trop vite mène souvent à la douleur et au découragement.",
          "Une reprise progressive permet à votre corps de se renforcer en sécurité, tout en installant une habitude durable.",
        ],
      },
      {
        h: "Les freins les plus courants",
        p: [
          "• La peur de se blesser ou d'avoir mal.",
          "• Le manque de temps ou de motivation pour démarrer seul.",
          "• Ne pas savoir par où commencer ni à quel rythme.",
          "• Une ancienne blessure ou des douleurs persistantes.",
        ],
      },
      {
        h: "La reprise sécurisée chez B.health",
        p: [
          "B.health commence par un bilan pour comprendre votre niveau, vos antécédents et vos objectifs. Si besoin, l'équipe de kinésithérapie et d'ostéopathie intervient pour préparer et sécuriser la reprise.",
          "Ensuite, coachs et activité physique adaptée prennent le relais avec un programme progressif. Vous avancez encadré, sans pression, dans un cadre bienveillant.",
        ],
      },
      {
        h: "Seniors et reprise d'activité",
        p: [
          "Il n'y a pas d'âge pour reprendre. L'activité physique adaptée aide à préserver la mobilité, l'équilibre, l'autonomie et l'énergie au quotidien.",
          "L'accompagnement de B.health est pensé pour rendre cette reprise accessible, motivante et sûre, y compris après 60 ou 70 ans.",
        ],
      },
      {
        h: "Votre première étape",
        p: [
          "Faites le test Restart en 3 minutes pour découvrir votre profil (mobilité, énergie…) et recevoir des conseils personnalisés. Notre équipe vous proposera ensuite votre bilan, sans engagement.",
        ],
      },
    ],
    faqs: [
      {
        q: "J'ai mal au dos, puis-je quand même reprendre ?",
        a: "Souvent oui, à condition d'être encadré. Chez B.health, l'équipe paramédicale (kiné, ostéo) peut préparer la reprise et adapter les exercices. En cas de douleur importante ou persistante, un avis professionnel est recommandé avant de reprendre.",
      },
      {
        q: "À quel rythme reprendre le sport ?",
        a: "Le bon rythme est celui qui est progressif et régulier, adapté à votre niveau de départ. C'est précisément ce que définit votre bilan et votre programme personnalisé chez B.health.",
      },
      {
        q: "Est-ce adapté aux seniors ?",
        a: "Oui. L'activité physique adaptée proposée par B.health convient à tous les âges et aide les seniors à préserver mobilité, équilibre, autonomie et énergie en toute sécurité.",
      },
    ],
  },
};

export const PILLAR_LIST = Object.values(PILLARS).map((p) => ({
  slug: p.slug,
  h1: p.h1,
  eyebrow: p.eyebrow,
}));

export function buildPillarJsonLd(p) {
  const url = `${SITE}/${p.slug}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: p.h1,
        description: p.description,
        image: p.image,
        inLanguage: "fr-BE",
        mainEntityOfPage: url,
        author: { "@type": "Organization", name: "B.health®" },
        publisher: {
          "@type": "Organization",
          name: "B.health®",
          logo: { "@type": "ImageObject", url: `${SITE}/logo-bhealth.png` },
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: p.h1, item: url },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: p.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };
}

export const PILLAR_SITE = SITE;
